import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showSuccessToast } from "../../../components/toast/toastBus";
import { uploadFile } from "../../../services/fileService";

import {
    fetchArmasAll,
    fetchCaceriaById,
    fetchCaceriasAll,
    fetchEventosAll,
    fetchPersonasAll,
    fetchRolesAll,
    fetchTipoCaceriaEnum,
} from "../api/catalogosApi";

import { createCaceria, updateCaceria, deleteCaceria, duplicateCaceria } from "../api/caceriasApi";

import { getRolNameById, personasFiltradasPara, validateParticipationRules } from "../domain/caceriasRules";
import { decorateCaceriasWithWarnings, buildCaceriaEventoMap, deriveYears, filterCacerias, deriveKpis, isCaceriaPasada } from "../domain/caceriasDerived";

export function useCacerias(initialCaceriaId) {
    /* ======= DATA ======= */
    const [cacerias, setCacerias] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [roles, setRoles] = useState([]);
    const [armas, setArmas] = useState([]);
    const [tipoCaceria, setTipoCaceria] = useState([]);
    const [eventos, setEventos] = useState([]);

    /* ======= UI ======= */
    const [q, setQ] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    // "pendientes" (por defecto) muestra solo las que no han pasado; "pasados" el histórico.
    const [estadoTiempo, setEstadoTiempo] = useState("pendientes");
    const [openSet, setOpenSet] = useState(new Set());

    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [caceriaToDelete, setCaceriaToDelete] = useState(null);
    const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "" });
    const [caceriaToEdit, setCaceriaToEdit] = useState(null);

    const [fileNew, setFileNew] = useState(null);
    const [fileEdit, setFileEdit] = useState(null);

    const armasIndex = useMemo(() => {
        const norm = (s) => String(s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        return (armas || []).map((a) => {
            const tipo = a.tipoArmaDescripcion ?? a.tipoArma?.descripcion ?? a.tipoArma ?? "";
            const calibre = a.calibre ?? "";
            const serie = a.numeroSerie ?? a.serie ?? "";

            const label = [
                tipo,
                calibre && `(${calibre})`,
                serie && `Serie: ${serie}`,
            ].filter(Boolean).join(" • ");

            const search = norm([
                tipo, calibre, serie,
            ].filter(Boolean).join(" "));

            return {
                ...a,
                _id: a.id,        // clave: el ArmaSearchSelect busca por _id
                _label: label,    // clave: lo que se muestra en el dropdown
                _search: search,  // clave: lo que se usa para filtrar
            };
        });
    }, [armas]);


    const [newCaceriaData, setNewCaceriaData] = useState({
        fecha: "",
        nombre: "",
        rutaDocumento: "",
        enlaces: [],
        tipoCaceria: "MONTERIA",
        eventoId: null,
        participaciones: [],
    });

    const toggleOpen = (id) =>
        setOpenSet((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });

    const isOpen = (id) => openSet.has(id);

    /* ======= FETCH ======= */
    // Catálogos: no dependen de la pestaña Pendientes/Pasados, se cargan una sola vez.
    const fetchCatalogos = useCallback(async () => {
        try {
            const [personasData, rolesData, armasData, eventoData] = await Promise.all([
                fetchPersonasAll(),
                fetchRolesAll(),
                fetchArmasAll(),
                fetchEventosAll(),
            ]);
            setPersonas(Array.isArray(personasData) ? personasData : []);
            setRoles(Array.isArray(rolesData) ? rolesData : []);
            setArmas(Array.isArray(armasData) ? armasData : []);
            setEventos(Array.isArray(eventoData) ? eventoData : []);
            return personasData;
        } catch (err) {
            console.error("Error al cargar catálogos:", err);
            alert("No se pudieron cargar los datos. Por favor, intente de nuevo más tarde.");
            return [];
        }
    }, []);

    // Cacerías: sí depende de la pestaña; el servidor filtra por fecha para no
    // traer siempre todo el histórico (era la causa principal de la lentitud).
    const fetchCacerias = useCallback(
        async (estado, personasParaDecorar) => {
            try {
                const caceriasData = await fetchCaceriasAll(estado);
                const updated = decorateCaceriasWithWarnings({
                    cacerias: Array.isArray(caceriasData) ? caceriasData : [],
                    personas: Array.isArray(personasParaDecorar) ? personasParaDecorar : personas,
                });
                setCacerias(updated);
            } catch (err) {
                console.error("Error al cargar cacerías:", err);
                alert("No se pudieron cargar las cacerías. Por favor, intente de nuevo más tarde.");
            }
        },
        [personas]
    );

    // Refresca cacerías + catálogos (tras crear/editar/eliminar/duplicar).
    const fetchAllData = useCallback(async () => {
        const personasData = await fetchCatalogos();
        await fetchCacerias(estadoTiempo, personasData);
    }, [fetchCatalogos, fetchCacerias, estadoTiempo]);

    useEffect(() => {
        (async () => {
            try {
                const data = await fetchTipoCaceriaEnum();
                setTipoCaceria(Array.isArray(data) ? data : []);
            } catch {
                setTipoCaceria([]);
            }
        })();
        fetchCatalogos();
        // Solo al montar: los catálogos no dependen de la pestaña.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cacerías: al montar y cada vez que cambia la pestaña Pendientes/Pasados.
    useEffect(() => {
        fetchCacerias(estadoTiempo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estadoTiempo]);

    /* ======= DERIVADOS ======= */
    const caceriaEventoMap = useMemo(() => buildCaceriaEventoMap(eventos), [eventos]);
    const years = useMemo(() => deriveYears(cacerias), [cacerias]);

    const list = useMemo(
        () => filterCacerias({ cacerias, q, selectedYear, eventos, caceriaEventoMap }),
        [cacerias, q, selectedYear, eventos, caceriaEventoMap]
    );

    const rolCazadorId = useMemo(() => {
        const r = roles.find((x) => String(x.rol).toUpperCase() === "CAZADOR");
        return r ? String(r.id) : "";
    }, [roles]);


    const { kpiTotal, kpiConAlerta, kpiParticipantes } = useMemo(() => deriveKpis(list), [list]);

    /* ======= HELPERS UI ======= */
    const armaLabel = (arma) => {
        if (!arma) return "—";

        const tipoRaw = String(arma.tipoArmaDescripcion ?? arma.tipo ?? "").trim();
        const tipo3 = tipoRaw ? tipoRaw.slice(0, 3) : "—";

        const calibre = String(arma.calibre ?? "—").trim() || "—";
        const serie = String(arma.numeroSerie ?? "—").trim() || "—";

        return `${tipo3} • ${calibre} • ${serie}`;
    };


    const personasFiltradasParaIndex = (parts, index) =>
        personasFiltradasPara({ roles, personas, parts, index });

    /* ======= FORM HANDLERS NUEVA ======= */
    const handleNewCaceriaChange = (e) => {
        const { name, value } = e.target;
        setNewCaceriaData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddParticipationField = () => {
        setNewCaceriaData((prev) => ({
            ...prev,
            participaciones: [
                ...prev.participaciones,
                { persona: "", rol: rolCazadorId, arma1: "", arma2: "", secretarioId: "", cargadorId: "" },
            ],
        }));
    };

    const handleRemoveParticipationField = (i) => {
        setNewCaceriaData((prev) => ({
            ...prev,
            participaciones: prev.participaciones.filter((_, idx) => idx !== i),
        }));
    };

    const handleParticipationChange = (index, field, value) => {
        setNewCaceriaData((prev) => {
            const arr = [...(prev.participaciones || [])];
            const base = { persona: "", rol: rolCazadorId, arma1: "", arma2: "", secretarioId: "", cargadorId: "" };
            arr[index] = { ...base, ...(arr[index] || {}) };
            arr[index][field] = value;
            return { ...prev, participaciones: arr };
        });
    };

    /* ======= ENLACES (OneDrive/SharePoint) — NUEVA ======= */
    const handleAddEnlace = () => {
        setNewCaceriaData((prev) => ({ ...prev, enlaces: [...(prev.enlaces || []), ""] }));
    };

    const handleRemoveEnlace = (i) => {
        setNewCaceriaData((prev) => ({
            ...prev,
            enlaces: (prev.enlaces || []).filter((_, idx) => idx !== i),
        }));
    };

    const handleEnlaceChange = (i, value) => {
        setNewCaceriaData((prev) => {
            const arr = [...(prev.enlaces || [])];
            arr[i] = value;
            return { ...prev, enlaces: arr };
        });
    };

    const handleSubmitNewCaceria = async (e) => {
        e.preventDefault();
        try {
            if (!validateParticipationRules(roles, newCaceriaData.participaciones)) return;

            let rutaDocumento = newCaceriaData.rutaDocumento || null;
            if (fileNew) {
                const uploadRes = await uploadFile(fileNew, "cacerias/documentos");
                rutaDocumento = uploadRes?.url || rutaDocumento;
            }

            const participaciones = newCaceriaData.participaciones.map((p) => ({
                personaId: parseInt(p.persona),
                rolId: parseInt(p.rol),
                arma1Id: p.arma1 ? parseInt(p.arma1) : null,
                arma2Id: p.arma2 ? parseInt(p.arma2) : null,
                secretarioId: p.secretarioId ? parseInt(p.secretarioId) : null,
                cargadorId: p.cargadorId ? parseInt(p.cargadorId) : null,
            }));

            const enlaces = (newCaceriaData.enlaces || []).map((s) => s.trim()).filter(Boolean);

            await createCaceria({
                fecha: newCaceriaData.fecha,
                nombre: (newCaceriaData.nombre || "").trim() || null,
                rutaDocumento,
                enlaces,
                tipoCaceria: newCaceriaData.tipoCaceria,
                eventoId: newCaceriaData.eventoId ?? null,
                participaciones,
            });

            await fetchAllData();
            setIsAddOpen(false);
            setNewCaceriaData({ fecha: "", nombre: "", rutaDocumento: "", enlaces: [], tipoCaceria: "MONTERIA", participaciones: [] });
            setFileNew(null);
            showSuccessToast("Cacería creada con éxito");
        } catch (err) {
            alert("No se pudo crear la cacería: " + (err?.message || "Error"));
        }
    };

    /* ======= EDIT ======= */
    const handleEditCaceria = (caceria) => {
        const parts = caceria.participaciones || [];
        const hunterParts = parts.filter((p) => p.rol?.rol === "CAZADOR");

        const caceriaForEdit = {
            ...caceria,
            eventoId: caceria.eventoId ?? caceria.evento?.id ?? null, // <-- AÑADIR
            fecha: String(caceria.fecha).split("T")[0],
            nombre: caceria.nombre || "",
            rutaDocumento: caceria.rutaDocumento || "",
            enlaces: caceria.enlaces ? [...caceria.enlaces] : [],
            participaciones: hunterParts.map((p) => ({
                id: p.id,
                persona: p.persona.id,
                rol: p.rol.id,
                arma1: p.arma1 ? p.arma1.id : "",
                arma2: p.arma2 ? p.arma2.id : "",
                secretarioId: p.secretario ? p.secretario.id : "",
                cargadorId: p.cargador ? p.cargador.id : "",
            })),
        };

        setCaceriaToEdit(caceriaForEdit);
        setIsEditOpen(true);
    };

    // Al llegar con un id en la URL (ej. desde el calendario), pedir SOLO esa
    // cacería (no esperar a que cargue el listado completo) y abrir edición.
    const autoEditConsumedRef = useRef(null);
    useEffect(() => {
        if (!initialCaceriaId || autoEditConsumedRef.current === initialCaceriaId) return;
        autoEditConsumedRef.current = initialCaceriaId;

        (async () => {
            try {
                const found = await fetchCaceriaById(initialCaceriaId);
                if (!found) return;
                const year = new Date(found.fecha).getFullYear();
                if (!Number.isNaN(year)) setSelectedYear(year);
                setEstadoTiempo(isCaceriaPasada(found) ? "pasados" : "pendientes");
                handleEditCaceria(found);
            } catch (err) {
                console.error("No se pudo cargar la cacería:", err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCaceriaId]);

    const handleEditCaceriaChange = (e) => {
        const { name, value } = e.target;
        setCaceriaToEdit((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddParticipationFieldEdit = () => {
        setCaceriaToEdit((prev) => ({
            ...prev,
            participaciones: [...(prev.participaciones || []), { persona: "", rol: rolCazadorId, arma1: "", arma2: "", secretarioId: "", cargadorId: "" }],
        }));
    };

    const handleRemoveParticipationFieldEdit = (i) => {
        setCaceriaToEdit((prev) => ({
            ...prev,
            participaciones: prev.participaciones.filter((_, idx) => idx !== i),
        }));
    };

    const handleParticipationChangeEdit = (index, field, value) => {
        setCaceriaToEdit((prev) => {
            const arr = [...(prev.participaciones || [])];
            const base = { persona: "", rol: rolCazadorId, arma1: "", arma2: "", secretarioId: "", cargadorId: "" };
            arr[index] = { ...base, ...(arr[index] || {}) };
            arr[index][field] = value;
            return { ...prev, participaciones: arr };
        });
    };

    /* ======= ENLACES (OneDrive/SharePoint) — EDITAR ======= */
    const handleAddEnlaceEdit = () => {
        setCaceriaToEdit((prev) => ({ ...prev, enlaces: [...(prev.enlaces || []), ""] }));
    };

    const handleRemoveEnlaceEdit = (i) => {
        setCaceriaToEdit((prev) => ({
            ...prev,
            enlaces: (prev.enlaces || []).filter((_, idx) => idx !== i),
        }));
    };

    const handleEnlaceChangeEdit = (i, value) => {
        setCaceriaToEdit((prev) => {
            const arr = [...(prev.enlaces || [])];
            arr[i] = value;
            return { ...prev, enlaces: arr };
        });
    };

    const handleSubmitEditCaceria = async (e) => {
        e.preventDefault();
        try {
            const onlyHunters = (caceriaToEdit.participaciones || []).filter(
                (p) => getRolNameById(roles, p.rol?.id ?? p.rol) === "CAZADOR"
            );
            if (!validateParticipationRules(roles, onlyHunters)) return;

            let rutaDocumento = caceriaToEdit.rutaDocumento || null;
            if (fileEdit) {
                const uploadRes = await uploadFile(fileEdit, "cacerias/documentos");
                rutaDocumento = uploadRes?.url || rutaDocumento;
            }

            const participaciones = onlyHunters.map((p) => ({
                id: p.id || null,
                personaId: parseInt(p.persona?.id || p.persona),
                rolId: parseInt(p.rol?.id || p.rol),
                arma1Id: p.arma1 ? parseInt(p.arma1?.id || p.arma1) : null,
                arma2Id: p.arma2 ? parseInt(p.arma2?.id || p.arma2) : null,
                secretarioId: p.secretarioId ? parseInt(p.secretarioId) : null,
                cargadorId: p.cargadorId ? parseInt(p.cargadorId) : null,
            }));

            const fecha = caceriaToEdit.fecha instanceof Date
                ? caceriaToEdit.fecha.toISOString().split("T")[0]
                : caceriaToEdit.fecha;

            const enlaces = (caceriaToEdit.enlaces || []).map((s) => s.trim()).filter(Boolean);

            await updateCaceria(caceriaToEdit.id, {
                ...caceriaToEdit,
                fecha,
                nombre: (caceriaToEdit.nombre || "").trim() || null,
                rutaDocumento,
                enlaces,
                eventoId: caceriaToEdit.eventoId ?? caceriaToEdit.evento?.id ?? null, // <-- AÑADIR
                participaciones,
            });

            await fetchAllData();
            setIsEditOpen(false);
            setCaceriaToEdit(null);
            setFileEdit(null);
            showSuccessToast("Cacería actualizada con éxito");
        } catch (err) {
            alert("No se pudo actualizar la cacería: " + (err?.message || "Error"));
        }
    };

    /* ======= DELETE / DUPLICATE ======= */
    const handleDeleteCaceria = (caceria) => {
        setCaceriaToDelete(caceria);
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteCaceria = async () => {
        try {
            await deleteCaceria(caceriaToDelete.id);
            setShowDeleteConfirmation(false);
            setCaceriaToDelete(null);
            await fetchAllData();
        } catch (err) {
            alert("No se pudo eliminar la cacería: " + (err?.message || "Error"));
        }
    };

    const handleDuplicateCaceria = async (caceria) => {
        try {
            await duplicateCaceria(caceria.id);
            await fetchAllData();
        } catch (err) {
            alert("No se pudo duplicar la cacería: " + (err?.message || "Error"));
        }
    };

    return {
        // data
        cacerias,
        personas,
        roles,
        armas,
        tipoCaceria,
        eventos,
        armasIndex,
        // ui
        q,
        setQ,
        selectedYear,
        setSelectedYear,
        estadoTiempo,
        setEstadoTiempo,
        openSet,
        toggleOpen,
        isOpen,

        // modals
        isAddOpen,
        setIsAddOpen,
        isEditOpen,
        setIsEditOpen,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        caceriaToDelete,
        setCaceriaToDelete,
        infoModal,
        setInfoModal,

        // create/edit state
        newCaceriaData,
        setNewCaceriaData,
        caceriaToEdit,
        setCaceriaToEdit,
        fileNew,
        setFileNew,
        fileEdit,
        setFileEdit,

        // derived
        years,
        list,
        kpiTotal,
        kpiConAlerta,
        kpiParticipantes,
        caceriaEventoMap,

        // helpers
        armaLabel,
        personasFiltradasParaIndex,

        // handlers
        fetchAllData,
        handleNewCaceriaChange,
        handleAddParticipationField,
        handleRemoveParticipationField,
        handleParticipationChange,
        handleAddEnlace,
        handleRemoveEnlace,
        handleEnlaceChange,
        handleSubmitNewCaceria,

        handleEditCaceria,
        handleEditCaceriaChange,
        handleAddParticipationFieldEdit,
        handleRemoveParticipationFieldEdit,
        handleParticipationChangeEdit,
        handleAddEnlaceEdit,
        handleRemoveEnlaceEdit,
        handleEnlaceChangeEdit,
        handleSubmitEditCaceria,

        handleDeleteCaceria,
        confirmDeleteCaceria,
        handleDuplicateCaceria,
    };
}
